<template>
  <div class="message-blocks">
    <div v-for="(block, index) in blocks" :key="index" class="block-row">
      <chat-chart-block
        v-if="structuredBlocks[index] && structuredBlocks[index].chart"
        class="block block-structured"
        :class="{ 'block-structured--revealing': revealing }"
        :chart="structuredBlocks[index].chart"
        :table="structuredBlocks[index].table"
      />
      <chat-table-block
        v-else-if="structuredBlocks[index] && structuredBlocks[index].table"
        class="block block-structured"
        :class="{ 'block-structured--revealing': revealing }"
        :table="structuredBlocks[index].table"
      />
      <p v-else-if="block.type === 'text'" class="block block-text"><span
        v-for="(part, partIndex) in revealBlocks[index].parts"
        :key="partIndex"
        :class="{ bold: part.bold }"
      ><a
        v-if="part.href"
        class="block-link"
        :href="part.href"
        target="_blank"
        rel="noopener noreferrer"
      ><template v-for="(token, tokenIndex) in part.tokens"><span
        v-if="token.word"
        :key="tokenIndex"
        class="word"
        :style="{ '--reveal-delay': `${token.delayMs}ms` }"
      >{{ token.text }}</span><template v-else>{{ token.text }}</template></template></a><template v-else><template
        v-for="(token, tokenIndex) in part.tokens"
      ><span
        v-if="token.word"
        :key="tokenIndex"
        class="word"
        :style="{ '--reveal-delay': `${token.delayMs}ms` }"
      >{{ token.text }}</span><template v-else>{{ token.text }}</template></template></template></span></p>
      <div v-else-if="block.type === 'image' || block.type === 'inline-image'" class="block block-image-wrapper">
        <img
          class="block-image"
          :src="block.url"
          alt=""
          @load="$emit('media-loaded')"
          @click="$emit('image-open', block)"
        />
        <div v-if="block.sending" class="block-media-sending">
          <span class="block-media-spinner">
            <img class="block-media-spinner__arc" :src="require('@/assets/chat-bot/media-spinner.svg')" alt="" />
          </span>
        </div>
      </div>
      <a
        v-else-if="block.type === 'file'"
        class="block block-file"
        :href="block.url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span class="file-icon">📄</span>
        <span class="file-name">{{ block.name || FILE_FALLBACK_NAME }}</span>
      </a>
      <div v-else-if="block.type === 'video'" class="block block-video-wrapper">
        <video
          class="block-video"
          :src="block.url"
          controls
          controlslist="nodownload noplaybackrate"
          disablepictureinpicture
          playsinline
          preload="metadata"
          @loadedmetadata="$emit('media-loaded')"
        />
        <div v-if="block.sending" class="block-media-sending">
          <span class="block-media-spinner">
            <img class="block-media-spinner__arc" :src="require('@/assets/chat-bot/media-spinner.svg')" alt="" />
          </span>
        </div>
      </div>
      <div
        v-else-if="block.type === 'notice'"
        class="block block-notice"
        :class="block.variant ? `block-notice--${block.variant}` : ''"
      >
        <img
          v-if="block.variant === 'system'"
          class="block-notice__badge"
          :src="require('@/assets/chat-bot/team-badge.svg')"
          alt=""
          aria-hidden="true"
        />
        <span class="block-notice__text">{{ block.value }}</span>
      </div>

      <div v-else-if="block.type === 'csat'" class="block csat-card">
        <template v-if="block.status === 'submitted'">
          <div class="csat-thanks">{{ CSAT_THANKS_MESSAGE }}</div>
        </template>
        <template v-else>
          <div class="csat-title">{{ block.title }}</div>
          <div class="csat-stars">
            <button
              v-for="star in 5"
              :key="star"
              type="button"
              class="csat-star"
              :class="{ 'csat-star--on': star <= (block.score || 0) }"
              :aria-label="`${star}`"
              @mousedown.prevent
              @click="$emit('csat-score', { block, score: star })"
            >
              <svg class="csat-star__icon" viewBox="0 0 29.2887 28.0518" fill="currentColor" aria-hidden="true">
                <path :d="STAR_PATH" />
              </svg>
            </button>
          </div>
          <div class="csat-scale">
            <span class="csat-scale__label">{{ CSAT_SCALE_LOW_LABEL }}</span>
            <span class="csat-scale__label">{{ CSAT_SCALE_HIGH_LABEL }}</span>
          </div>
          <textarea
            class="csat-comment"
            :value="block.comment || ''"
            :placeholder="CSAT_COMMENT_PLACEHOLDER"
            rows="3"
            @touchstart="onCsatCommentTouchStart"
            @touchend="onCsatCommentTouchEnd"
            @focus="$emit('csat-comment-focus', $event.target)"
            @blur="$emit('csat-comment-blur')"
            @input="$emit('csat-comment', { block, comment: $event.target.value })"
          />
          <button
            type="button"
            class="csat-submit"
            :disabled="!block.score || block.status === 'loading'"
            @mousedown.prevent
            @click="$emit('csat-submit', block)"
          >{{ CSAT_SUBMIT_LABEL }}</button>
        </template>
      </div>

      <!-- Client-only interactive escalation blocks. These are appended by Chatbot
           (never accepted from the server — they are NOT in KNOWN_BLOCK_TYPES), so the
           server-content security model is unchanged. Rendered with native buttons that
           emit Vue events; no v-html, no URLs. -->
      <div v-else-if="block.type === 'escalation-consent'" class="block escalation-card">
        <div v-if="block.status === 'dismissed'" class="escalation-dismissed">{{ DISMISSED_TEXT }}</div>
        <template v-else>
          <div class="escalation-prompt">{{ CONSENT_PROMPT_TEXT }}</div>
          <div class="escalation-buttons">
            <button
              type="button"
              class="escalation-btn escalation-btn--primary"
              :disabled="block.status === 'loading'"
              @click="$emit('escalation-accept', block)"
            >{{ block.status === 'loading' ? LOADING_TEXT : ACCEPT_TEXT }}</button>
            <button
              type="button"
              class="escalation-btn escalation-btn--secondary"
              :disabled="block.status === 'loading'"
              @click="$emit('escalation-dismiss', block)"
            >{{ DISMISS_TEXT }}</button>
          </div>
          <div v-if="block.status === 'error'" class="escalation-error">{{ ERROR_TEXT }}</div>
        </template>
      </div>

      <div v-else-if="block.type === 'escalation-open-elsewhere'" class="block escalation-card">
        <div class="escalation-prompt">{{ OPEN_ELSEWHERE_TEXT }}</div>
        <button
          type="button"
          class="escalation-btn escalation-btn--primary escalation-btn--full"
          @click="$emit('escalation-open-elsewhere', block)"
        >{{ OPEN_ELSEWHERE_BUTTON_TEXT }}</button>
      </div>

      <div v-else-if="block.type === 'escalation-transferred'" class="block escalation-card">
        <div class="escalation-prompt">{{ TRANSFERRED_TEXT }}</div>
        <button
          type="button"
          class="escalation-btn escalation-btn--primary escalation-btn--full"
          :disabled="block.status === 'loading'"
          @click="$emit('escalation-open', block)"
        >{{ block.status === 'loading' ? LOADING_TEXT : OPEN_CONVERSATION_TEXT }}</button>
        <div v-if="block.status === 'error'" class="escalation-error">{{ ERROR_TEXT }}</div>
      </div>

      <span v-else class="block block-unknown">[unknown block type: {{ block.type }}]</span>
    </div>
  </div>
</template>

<script>
import {
  CSAT_COMMENT_PLACEHOLDER,
  CSAT_SCALE_HIGH_LABEL,
  CSAT_SCALE_LOW_LABEL,
  CSAT_SUBMIT_LABEL,
  CSAT_THANKS_MESSAGE,
  REVEAL_CONFIG,
} from '@/constants/chat';
// Text-only renderer for the strict JSON chat contract (see
// docs/CHAT-JSON-CONTRACT.md). Every server value flows through Vue's `{{ }}`
// interpolation which HTML-escapes by default — no v-html, no DOM parsing,
// no URL fetches from server content. Unknown block types fail closed with a
// visible placeholder.
//
// The `escalation-consent` / `escalation-transferred` blocks are the one
// exception: they are interactive, but they are CLIENT-GENERATED only
// (appended by Chatbot when the server flags shouldEscalate) and are NOT in
// the server-validated KNOWN_BLOCK_TYPES — so the server can never inject one.
// They render static Hebrew labels + native buttons that emit Vue events; no
// server-supplied content, no URLs.
//
// image and file are rendered too, but only ever pointing at our own proxy
// path (enforced in chat-validation.js's isValidBlock, not here) — never a
// host chosen by server content. button-card and audio remain reserved.
//
// A `text` block carrying a valid `table` / `chart` field (see
// chat-structured.js) renders as a table / chart instead of its `value`;
// cells and labels are still interpolated text, and charts draw on a canvas.

import { hasCoarsePointer } from '@/utils/deviceUtils';
import parseInline, { tokenizeForReveal } from './chat-formatting';
import { chartOf, tableOf } from './chat-structured';
import ChatChartBlock from './ChatChartBlock.vue';
import ChatTableBlock from './ChatTableBlock.vue';

// Figma: Flows for CX, node 1369:1515 — the star with its 2px corners.
const STAR_PATH = 'M12.7977 1.23194C13.4809 -0.410644 15.8078 -0.410648 16.491 1.23194L19.0685 7.42909C19.3565 8.12156 20.0078 8.5947 '
  + '20.7553 8.65463L27.4457 9.191C29.219 9.33316 29.9381 11.5462 28.587 12.7035L23.4896 17.0699C22.9201 17.5578 22.6713 18.3234 '
  + '22.8453 19.0529L24.4027 25.5815C24.8154 27.312 22.9329 28.6797 21.4147 27.7524L15.6869 24.2538C15.0468 23.8629 14.2419 23.8629 '
  + '13.6018 24.2538L7.87397 27.7524C6.35577 28.6797 4.47327 27.312 4.88604 25.5815L6.44336 19.0529C6.61738 18.3234 6.36863 17.5578 '
  + '5.79906 17.0699L0.701719 12.7035C-0.649355 11.5462 0.0696938 9.33316 1.843 9.191L8.53335 8.65463C9.28093 8.5947 9.93215 8.12156 '
  + '10.2202 7.42909L12.7977 1.23194Z';
const TAP_SLOP_PX = 10;

export default {
  name: 'MessageBlocks',
  components: { ChatChartBlock, ChatTableBlock },
  props: {
    blocks: {
      type: Array,
      required: true,
    },
    // True only for the one message whose words are currently blurring in.
    revealing: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'escalation-accept', 'escalation-dismiss', 'escalation-open', 'escalation-open-elsewhere',
    'csat-score', 'csat-comment', 'csat-comment-focus', 'csat-comment-blur', 'csat-submit', 'image-open', 'media-loaded',
  ],
  data() {
    return {
      CSAT_COMMENT_PLACEHOLDER,
      CSAT_SCALE_HIGH_LABEL,
      CSAT_SCALE_LOW_LABEL,
      CSAT_SUBMIT_LABEL,
      CSAT_THANKS_MESSAGE,
      CONSENT_PROMPT_TEXT: 'להעביר אותך לשיחה עם הצוות?',
      ACCEPT_TEXT: 'כן',
      DISMISS_TEXT: 'לא, תודה',
      LOADING_TEXT: 'פותח צ׳אט עם נציג',
      DISMISSED_TEXT: 'השיחה לא הועברה לטיפול הצוות.',
      ERROR_TEXT: 'משהו השתבש בפתיחת השיחה עם הנציג. אפשר לנסות שוב',
      TRANSFERRED_TEXT: 'השיחה עברה לטיפול הצוות',
      OPEN_CONVERSATION_TEXT: 'לפתיחת השיחה',
      OPEN_ELSEWHERE_TEXT: 'יש לך שיחה פתוחה עם הצוות',
      OPEN_ELSEWHERE_BUTTON_TEXT: 'אני רוצה לעבור אליה',
      FILE_FALLBACK_NAME: 'קובץ',
      STAR_PATH,
    };
  },
  computed: {
    // { chart, table } for blocks that render as a chart or table, else null.
    structuredBlocks() {
      return this.blocks.map(block => {
        const chart = chartOf(block);
        const table = tableOf(block);
        return chart || table ? { chart, table } : null;
      });
    },
    // Only the one revealing message pays the per-word span cost; a settled
    // message renders each part as a single plain token, keeping long threads
    // free of thousands of spans whose animation already played.
    revealBlocks() {
      if (this.revealing) {
        return tokenizeForReveal(this.blocks, REVEAL_CONFIG);
      }
      return this.blocks.map((block, index) => (block.type === 'text' && !this.structuredBlocks[index]
        ? { parts: parseInline(block.value).map(part => ({ ...part, tokens: [{ text: part.text }] })) }
        : null));
    },
  },
  methods: {
    onCsatCommentTouchStart(event) {
      const touch = event.touches[0];
      this.csatTouchStart = { x: touch.clientX, y: touch.clientY };
    },
    // iOS reveals a focused field by panning the page and scrolling the thread
    // in steps as the keyboard rises; the chat places the card itself instead.
    onCsatCommentTouchEnd(event) {
      const field = event.target;
      const touch = event.changedTouches[0];
      const start = this.csatTouchStart;
      this.csatTouchStart = null;
      if (!start || !event.cancelable || document.activeElement === field || !hasCoarsePointer()) { return; }
      if (Math.abs(touch.clientX - start.x) > TAP_SLOP_PX || Math.abs(touch.clientY - start.y) > TAP_SLOP_PX) { return; }
      event.preventDefault();
      field.focus({ preventScroll: true });
    },
  },
};
</script>

<style scoped lang="scss">
.message-blocks {
  direction: rtl;
  text-align: right;
}

.block-row {
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.block-text {
  margin: 0;
  max-width: 100%;
  font-size: 16px;
  line-height: 24px;
  color: #202125;
  white-space: pre-wrap;
  word-wrap: break-word;
  -webkit-font-smoothing: subpixel-antialiased;
  -moz-osx-font-smoothing: auto;

  .bold {
    font-weight: 600;
  }

  .block-link {
    color: #5d7afd;
    text-decoration: underline;
    overflow-wrap: anywhere;
  }
}

/* Tables and charts land whole; they fade in with the reply's words instead of word by word. */
.block-structured--revealing {
  animation: revealStructured 420ms ease-out both;
}

@keyframes revealStructured {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .block-structured--revealing {
    animation: none;
  }
}

.block-unknown {
  display: inline-block;
  font-size: 13px;
  color: #c33;
  font-style: italic;
}

.block-image-wrapper {
  position: relative;
  display: inline-block;
}

/* Figma: Flows for CX, node 1369:1267. */
.block-image {
  display: block;
  max-width: 220px;
  max-height: 297px;
  width: auto;
  height: auto;
  border: 1px solid rgba(32, 33, 37, 0.15);
  border-radius: 16px;
  cursor: pointer;
}

.block-file {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 260px;
  padding: 12px 16px;
  background-color: #F5F5F5;
  border-radius: 12px;
  color: #202125;
  text-decoration: none;
}

.file-icon {
  flex-shrink: 0;
  font-size: 22px;
  line-height: 1;
}

.file-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 15px;
}

.block-video-wrapper {
  position: relative;
  display: inline-block;
}

.block-media-sending {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background-color: rgba(32, 33, 37, 0.2);
  pointer-events: none;
}

/* Shrinks inside media smaller than the 58px arc, e.g. a short banner screenshot. */
.block-media-spinner {
  display: block;
  width: min(58px, 60%);
  height: min(58px, 60%);
}

.block-media-spinner__arc {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: block-media-spin 1s linear infinite;
}

@keyframes block-media-spin {
  to { transform: rotate(360deg); }
}

.block-video {
  display: block;
  max-width: 260px;
  max-height: 320px;
  width: 100%;
  border-radius: 16px;
}

/* Notice block — V1's validation/rate-limit/error message styling. */
.block-notice {
  width: 95%;
  padding: 20px;
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  direction: rtl;
  color: #202125;
}

.block-notice--validation,
.block-notice--error {
  background-color: #FFCEB9;
}

.block-notice--rate-limit {
  background-color: #FFEFC3;
}

/* Figma: Flows for CX, node 1365:989 — a strip marking a change of who is
   answering, so it is deliberately not styled as an error. Figma draws it 46px
   past both screen edges (corners off-screen), on top of the message list's
   side padding. */
.block-notice--system {
  --bleed: calc(var(--messages-side-padding, 20px) + 46px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: calc(100% + 2 * var(--bleed));
  margin: 0 calc(-1 * var(--bleed));
  background-color: #5D7AFD;
  color: #FFFFFF;
  border-radius: 14px;
  padding: 12px 21px 14px;
  font-size: 14px;
  line-height: 20px;
}

/* Figma fixes the bar at 46px, so the 22px badge overhangs the 20px text row. */
.block-notice__badge {
  width: 22px;
  height: 22px;
  margin: -1px 0;
  flex-shrink: 0;
}

/* Figma: Flows for CX, node 1369:1511 — the card shown once the team closes the
   conversation. Replaces Intercom's own rating, which the messenger used to ask
   for and which cannot be submitted through their API. */
.csat-card {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #ffffff;
  border-radius: 25px;
  box-shadow: 0 5px 9.9px rgba(0, 0, 0, 0.05);
  padding: 32px 20px;
  margin: 6px 0;
}

.csat-title {
  font-size: 16px;
  line-height: 24px;
  color: #202125;
  text-align: center;
  margin-bottom: 24px;
}

.csat-stars {
  display: flex;
  gap: 19px;
  margin-bottom: 5px;
}

/* Figma: Flows for CX, node 1369:1515. The star sits off-centre in its 37px box. */
.csat-star {
  width: 37px;
  height: 37px;
  box-sizing: border-box;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 3.2px 3.9px 5.7px;
  color: #d9d9d9;
  opacity: 0.58;
}

.csat-star__icon {
  display: block;
  width: 100%;
  height: 100%;
}

/* Figma: Flows for CX, nodes 1369:1513-1514. Same width as the star row, so
   each label centres under its outer star. */
.csat-scale {
  display: flex;
  justify-content: space-between;
  width: 261px;
  margin-bottom: 20px;
}

.csat-scale__label {
  width: 37px;
  font-size: 12px;
  line-height: 16px;
  color: #202125;
  text-align: center;
}

.csat-star--on {
  color: #ffc531;
  opacity: 1;
}

.csat-comment {
  width: 297px;
  max-width: 100%;
  height: 116px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  border-radius: 14px;
  padding: 16px 16px 12px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  direction: rtl;
  text-align: right;
  resize: none;
  margin-bottom: 20px;

  &::placeholder {
    opacity: 0.8;
  }
}

.csat-submit {
  width: 303px;
  max-width: 100%;
  height: 50px;
  border: none;
  border-radius: 13px;
  background-color: #5d7afd;
  color: #ffffff;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  cursor: pointer;
}

.csat-submit:disabled {
  opacity: 0.4;
  cursor: default;
}

.csat-thanks {
  font-size: 18px;
  line-height: 26px;
  color: #202125;
  text-align: center;
}

/* Escalation consent / transferred cards — mirror V1's escalation styling. */
.escalation-card {
  width: 100%;
  box-sizing: border-box;
  background-color: #DCE8F6;
  border-radius: 8px;
  padding: 24px 20px;
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.escalation-prompt {
  font-size: 16px;
  line-height: 24px;
  color: #202125;
  text-align: center;
}

.escalation-buttons {
  display: flex;
  direction: rtl;
  gap: 12px;
  justify-content: center;
}

.escalation-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 24px;
  border-radius: 16px;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}

.escalation-btn--primary {
  min-width: 89px;
  background-color: #5D7AFD;
  color: #FFFFFF;
  border: none;
}

.escalation-btn--secondary {
  min-width: 94px;
  background-color: transparent;
  color: #5D7AFD;
  border: 1px solid #5D7AFD;
}

.escalation-btn--full {
  width: 100%;
}

.escalation-dismissed {
  font-size: 16px;
  color: #202125;
  text-align: center;
}

.escalation-error {
  font-size: 14px;
  color: #C24A2C;
  text-align: center;
}
</style>
